import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';

function JobDetails() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadJobDetails();
    
    // Auto-refresh every 3 seconds for processing jobs
    const interval = setInterval(loadJobDetails, 3000);
    return () => clearInterval(interval);
  }, [jobId]);

  const loadJobDetails = async () => {
    try {
      const response = await api.get(`/pdf-jobs/${jobId}`);
      setJobDetails(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load job details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelJob = async () => {
    if (!window.confirm('Are you sure you want to cancel this job?')) {
      return;
    }

    try {
      await api.delete(`/pdf-jobs/${jobId}`);
      loadJobDetails(); // Refresh
    } catch (err) {
      setError('Failed to cancel job');
      console.error(err);
    }
  };

  const downloadJobFiles = async () => {
    try {
      const response = await api.get(`/pdf-jobs/${jobId}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `pdf_job_${jobId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to download files');
      console.error(err);
    }
  };

  const downloadSingleFile = async (fileId) => {
    try {
      const response = await api.get(`/download/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_${fileId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to download file');
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getProgressPercentage = () => {
    if (!jobDetails?.job.total_batches) return 0;
    return Math.round((jobDetails.job.completed_batches / jobDetails.job.total_batches) * 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!jobDetails) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h1>
          <Link
            to="/jobs"
            className="text-blue-600 hover:text-blue-500"
          >
            ← Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  const { job, generated_files } = jobDetails;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Job #{job.id} Details
        </h1>
        <div className="flex space-x-3">
          {job.status === 'completed' && (
            <button
              onClick={downloadJobFiles}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Download All (ZIP)
            </button>
          )}
          {job.status === 'processing' && (
            <button
              onClick={cancelJob}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Cancel Job
            </button>
          )}
          <Link
            to="/jobs"
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Back to Jobs
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Job Overview */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Job Overview</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
              {job.status.toUpperCase()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="text-sm font-medium text-gray-500">Created</div>
              <div className="mt-1 text-sm text-gray-900">{formatDate(job.created_at)}</div>
            </div>
            {job.completed_at && (
              <div>
                <div className="text-sm font-medium text-gray-500">Completed</div>
                <div className="mt-1 text-sm text-gray-900">{formatDate(job.completed_at)}</div>
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-gray-500">Total Records</div>
              <div className="mt-1 text-sm text-gray-900">{job.total_records?.toLocaleString() || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Records per Batch</div>
              <div className="mt-1 text-sm text-gray-900">{job.records_per_batch?.toLocaleString()}</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm font-medium text-gray-500">Total Batches</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">{job.total_batches || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Completed Batches</div>
              <div className="mt-1 text-lg font-semibold text-green-600">{job.completed_batches || 0}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Progress</div>
              <div className="mt-1 text-lg font-semibold text-blue-600">{getProgressPercentage()}%</div>
            </div>
          </div>

          {job.status === 'processing' && job.total_batches && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Progress</span>
                <span>{getProgressPercentage()}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generated Files */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4">
          <h2 className="text-xl font-semibold mb-4">Generated Files</h2>
          
          {generated_files && generated_files.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {generated_files.map((file) => (
                    <tr key={file.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Batch #{file.batch_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                          {file.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(file.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {file.status === 'completed' ? (
                          <button
                            onClick={() => downloadSingleFile(file.id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Download
                          </button>
                        ) : file.status === 'failed' ? (
                          <span className="text-red-600" title={file.error_message}>
                            Failed
                          </span>
                        ) : (
                          <span className="text-gray-500">Processing...</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500">
                {job.status === 'pending' ? 'Job is waiting to be processed...' : 'No files generated yet.'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {generated_files && generated_files.some(f => f.status === 'failed') && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-red-800 mb-2">Failed Batches</h3>
          <div className="space-y-2">
            {generated_files
              .filter(f => f.status === 'failed')
              .map(file => (
                <div key={file.id} className="text-sm text-red-700">
                  <strong>Batch #{file.batch_number}:</strong> {file.error_message || 'Unknown error'}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default JobDetails;
